import { DragDropContext, Draggable, type DropResult, Droppable } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Divider, TextField, Typography, useTheme } from '@mui/material';
import { FieldArray, type FormikErrors, type FormikTouched, getIn, useFormikContext } from 'formik';
import { BoxAdd, Trash } from 'iconsax-react';
import LogRocket from 'logrocket';
import {
  type MutableRefObject,
  type ReactElement,
  memo,
  useContext,
  useEffect,
  useState,
} from 'react';

import { PRODUCT_DELIVERY_TYPE_OPTIONS, getUserFacingCategoryName } from '@core/constants/product';
import { SlabContext } from '@core/context/SlabContext';
import type { ProjectOrQuoteProductFormikType } from '@core/formik/projectOrQuoteProduct';
import {
  type ProductSectionType,
  type SharedFormikType,
  createProductSectionActions,
} from '@core/hooks/useProductSection';
import Enums from '@core/types/api/Enums';
import type { Product } from '@core/types/api/Product/Product';
import type { Project } from '@core/types/api/Project/Project';
import type { ProjectProduct } from '@core/types/api/ProjectProduct/ProjectProduct';
import { cleanedDecimal, formatDecimalString } from '@core/utils/decimals';
import { grossPriceOf, totalDiscountOff } from '@core/utils/discounts';
import { hasStringValue, hasValue } from '@core/utils/equality';
import {
  forecastableCategoryDisplayList,
  isForecastedProductCategory,
} from '@core/utils/forecastDistribution';
import { getCurrencySymbol, resolveCurrencyCode } from '@core/utils/format';
import { SetFormikValue } from '@core/utils/formikHelpers';
import { requiredLabel } from '@core/utils/strings';
import { calculateTierAdjustmentFormik } from '@core/utils/tierPricing';
import { getUnitLabel, getUnitLabelWithSlash } from '@core/utils/unitHelpers';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import { useConfirmDialog } from '@web/providers/ConfirmProvider/useConfirmDialog';

import { useTenantMargins } from '../../hooks/useTenantMargins';
import { lookups } from '../../utils/DomainHelpers';
import { WithDeliveryProductCategory } from '../../utils/WithDeliveryPriceCalculations';
import { Input } from '../Input/Input';
import { StyledSwitch } from '../InputSwitch/InputSwitch';
import { ApiLookupInput } from '../LookupInput/ApiLookupInput';
import { LocalLookupInput } from '../LookupInput/LocalLookupInput';
import type { LookupInputOption } from '../LookupInput/LookupInputSharedComponents';
import { PermissionGuard } from '../Permissions/PermissionGuard';
import { QuoteProductDiscountPicker } from '../QuoteProductDiscounts/QuoteProductDiscountPicker';
import { SingleUnitDurationInput } from '../SingleUnitDurationInput/SingleUnitDurationInput';
import { StatusBox, errorKind, warningKind } from '../StatusBox/StatusBox';
import { ProductTruckTypesList } from './components/ProductTruckTypesList';
import { UnitPrice } from './components/UnitPrice';

const Memo = memo(
  ({ children }: { children: ReactElement; value: any }): ReactElement => children,
  (prev, next) => prev.value === next.value,
);

export type ProductSectionProps = {
  sectionType: Enums.QuoteProductKind;
  plant: { id: string | null } | undefined;
  /**
   * When using this prop at the Project Product level, provide `null`.
   * In quick-quote mode with no project, provide `undefined`.
   */
  project: Project | null | undefined;
  curProductsRef: MutableRefObject<Record<string, Product>>;
  /** When using this prop at the Project Product level, provide an empty record. */
  projectProductsByProductID: Record<string, ProjectProduct>;
  plantIndex?: number;
  readOnly?: boolean;
  isPriceSettled?: boolean;
};

export const ProductSection = ({
  sectionType,
  plant,
  project,
  curProductsRef,
  projectProductsByProductID,
  plantIndex,
  readOnly = false,
  isPriceSettled = true,
}: ProductSectionProps): ReactElement => {
  // Get tenant settings for margin configuration and target margin display
  const { defaultIsMoneySwitchOn, marginConfiguration, defaultMargins } = useTenantMargins();
  const { userInfo } = useContext(SlabContext);
  const requiresProductDeliveryType = userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresProductDeliveryType,
  ]);
  const defaultToSuggestedPrice = userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagDefaultPriceToSuggestedPrice,
  ]);
  const hasTierPricing =
    userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagTierPricing]) &&
    userInfo.tenant.tierPricing;
  const hasDiscounts = userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagDiscounts]);

  const confirm = useConfirmDialog();

  const theme = useTheme();

  const formikBag = useFormikContext<SharedFormikType>();

  const companyId = formikBag.values.company?.id ?? null;

  const isQuoteSection = project !== null;

  const { data: fixedPricingByCompany } = useSlabQuery(
    'GET tier pricing by company ID',
    { queryParams: { companyIds: [companyId ?? ''] } },
    { enabled: hasTierPricing && hasStringValue(companyId) },
  );

  const [isDragEnd, setIsDragEnd] = useState<boolean>(false);

  const isProjectForm = project === null;

  const formikSectionType = ((): ProductSectionType => {
    // If project is null, we are at the Project level. So use 'productsByPlants'.
    if (hasValue(plantIndex) && isProjectForm) {
      return `productsByPlants.${plantIndex}.products`;
    }

    // For Quotes with plantIndex, use the nested structure
    if (hasValue(plantIndex)) {
      switch (sectionType) {
        case Enums.QuoteProductKind.Primary:
          return `mainProductsByPlants.${plantIndex}.products`;
        case Enums.QuoteProductKind.Additional:
          return `additionalProductsByPlants.${plantIndex}.products`;
        default:
          LogRocket.error(`Type passed to ProductSection with no match: ${sectionType}`);
          return `mainProductsByPlants.${plantIndex}.products`;
      }
    }

    // Legacy flat structure: Quotes now always use multi-location with plantIndex
    // If we reach here without plantIndex, use index 0 as default
    switch (sectionType) {
      case Enums.QuoteProductKind.Primary:
        return 'mainProductsByPlants.0.products';
      case Enums.QuoteProductKind.Additional:
        return 'additionalProductsByPlants.0.products';
      default:
        LogRocket.error(`Type passed to ProductSection with no match: ${sectionType}`);
        return 'mainProductsByPlants.0.products';
    }
  })();

  const formikProducts =
    formikBag.getFieldMeta<Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>[]>(
      formikSectionType,
    ).value;

  useEffect(() => {
    if (!isDragEnd) return;
    const formikErrors = formikBag.errors[formikSectionType] as FormikErrors<
      Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>
    >[];

    if (formikErrors === undefined) return;

    const reorderedErrors = formikProducts.map((_, index) => formikErrors[index]);

    formikBag.setErrors({
      ...formikBag.errors,
      [formikSectionType]: reorderedErrors,
    });

    const formikTouched = formikBag.touched[formikSectionType] as FormikTouched<
      Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>
    >[];

    if (formikTouched === undefined) return;

    const reorderedTouched = formikProducts.map((_, index) => formikTouched[index]);

    formikBag.setTouched({
      ...formikBag.touched,
      [formikSectionType]: reorderedTouched,
    });

    setIsDragEnd(false);
  }, [formikBag.errors, formikBag.touched, formikProducts]);

  const {
    actionsForIndex,
    actionsForAdditionalTruckingInfo,
    reorder,
    addRow,
    removeRow,
    addTruckingInfoRow,
  } = createProductSectionActions({
    formikBag,
    formikSectionType,
    sectionType,
    project,
    curProductsRef,
    projectProductsByProductID,
    marginConfiguration,
    defaultMargins,
    defaultToSuggestedPrice,
    fixedPricingByCompany,
    plantId: plant?.id ?? undefined,
  });

  const onDragEnd = (result: DropResult): void => {
    if (!hasValue(result.destination)) return;
    reorder(result.source.index, result.destination.index);
  };

  const handleRemoveProduct = (index: number, productName: string): void => {
    const selectedTruckingTypes = formikProducts[index].additionalTruckingInfos;

    if (selectedTruckingTypes.length > 0) {
      confirm.alert({
        title: 'Remove Product',
        description: `Remove ${productName} and all trucking configurations from this location?`,
        actions: [
          { label: 'Cancel', onClick: (): void => {}, variant: 'slab-secondary' },
          {
            label: 'Remove',
            onClick: (): void => {
              removeRow(index);
            },
            variant: 'slab-primary',
          },
        ],
      });
    } else {
      removeRow(index);
    }
  };

  return (
    <FieldArray
      name={formikSectionType}
      validateOnChange
      render={(): ReactElement => (
        <>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId='droppable'>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {formikProducts.map((formikProduct, index) => {
                    const isCurProductSelected = ((): boolean => {
                      const currentProductID = formikBag.getFieldMeta<string | null>(
                        `${formikSectionType}.${index}.product.id`,
                      );
                      return currentProductID.value !== null;
                    })();

                    const plantProduct = formikProduct.product.id
                      ? (curProductsRef.current[formikProduct.product.id] ?? null)
                      : null;

                    /**
                     * When true, the selected product's category is not one that
                     * participates in project forecasts (Mix/Aggregate/Asphalt),
                     * so we render an inline warning in the project editor.
                     */
                    const showNonForecastedWarning =
                      isProjectForm &&
                      plantProduct !== null &&
                      !isForecastedProductCategory(plantProduct.category);

                    // *** Per‑row closures from shared logic
                    const actions = actionsForIndex(index, plantProduct);

                    // Money fields use the product's plant currency, but an empty row
                    // has no product yet, so fall back to the tenant default.
                    const currencySymbol = getCurrencySymbol(
                      resolveCurrencyCode(plantProduct?.plant.currencyCode),
                    );

                    /** Display Only Inputs. */
                    const moneyDisplaySwitch = (
                      <Box
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                        paddingY={2}
                      >
                        <Box display='flex' alignItems='center'>
                          <Typography fontWeight='bold'>{currencySymbol}</Typography>
                          <StyledSwitch
                            disableStateColor
                            disabled={readOnly}
                            checked={!formikProduct.isMoneySwitchOn}
                            onChange={() =>
                              SetFormikValue(
                                formikBag,
                                `${formikSectionType}.${index}.isMoneySwitchOn`,
                                !formikProduct.isMoneySwitchOn,
                              )
                            }
                          />
                          <Typography fontWeight='normal'>%</Typography>
                        </Box>
                      </Box>
                    );
                    const marginOverMaterialsDisplay = (
                      <PermissionGuard permissions={[Enums.Permission.ViewCostData]}>
                        <Input
                          readOnly={readOnly}
                          sx={{ flex: 1, flexBasis: 120 }}
                          name={
                            formikProduct.isMoneySwitchOn
                              ? `${formikSectionType}.${index}.displayOnly.marginOverMaterialsCurrency`
                              : `${formikSectionType}.${index}.displayOnly.marginOverMaterialsPercentage`
                          }
                          label='MoM'
                          tip='Margin over materials'
                          disabled
                        />
                      </PermissionGuard>
                    );
                    const oneWayTripTimeDisplay = (
                      <SingleUnitDurationInput
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.deliveryCosts.displayOnly.adjustedDriveToJobTime`}
                        label='One-way trip time'
                        unit='minutes'
                        decimalScale={2}
                        disabled
                      />
                    );
                    const actualMarginDisplay = (
                      <PermissionGuard permissions={[Enums.Permission.ViewCostData]}>
                        <Input
                          readOnly={readOnly}
                          sx={{ flex: 1, flexBasis: 120 }}
                          name={
                            formikProduct.isMoneySwitchOn
                              ? `${formikSectionType}.${index}.displayOnly.actualMarginCurrency`
                              : `${formikSectionType}.${index}.displayOnly.actualMarginPercentage`
                          }
                          label='Actual margin'
                          disabled
                        />
                      </PermissionGuard>
                    );
                    const unitCostDisplay = (
                      <PermissionGuard permissions={[Enums.Permission.ViewCostData]}>
                        <Input
                          readOnly={readOnly}
                          sx={{ flex: 1, flexBasis: 120 }}
                          name={`${formikSectionType}.${index}.displayOnly.unitCost`}
                          label='Unit cost'
                          disabled
                        />
                      </PermissionGuard>
                    );
                    const listPriceDisplay = (
                      <Input
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.displayOnly.listPrice`}
                        label='List price'
                        disabled
                      />
                    );
                    const suggestedPriceDisplay = (
                      <PermissionGuard permissions={[Enums.Permission.ViewCostData]}>
                        <Input
                          readOnly={readOnly}
                          sx={{ flex: 1, flexBasis: 120 }}
                          name={`${formikSectionType}.${index}.displayOnly.suggestedPrice`}
                          label='Suggested price'
                          disabled
                        />
                      </PermissionGuard>
                    );
                    const targetMarginDisplay = (
                      <PermissionGuard permissions={[Enums.Permission.ViewCostData]}>
                        <Input
                          readOnly={readOnly}
                          sx={{ flex: 1, flexBasis: 120 }}
                          name={
                            formikProduct.isMoneySwitchOn
                              ? `${formikSectionType}.${index}.displayOnly.targetMarginCurrency`
                              : `${formikSectionType}.${index}.displayOnly.targetMarginPercentage`
                          }
                          label='Target margin'
                          disabled
                        />
                      </PermissionGuard>
                    );
                    const deliveryCostDisplay = (
                      <PermissionGuard permissions={[Enums.Permission.ViewCostData]}>
                        <TextField
                          inputProps={{
                            readOnly,
                          }}
                          sx={{ flex: 1, flexBasis: 120 }}
                          label='Delivery cost'
                          value={formikProduct.deliveryCosts?.displayOnly?.totalDeliveryCost ?? ''}
                          fullWidth
                          disabled
                        />
                      </PermissionGuard>
                    );

                    /** Basic Inputs. */
                    const externalNameInput = (
                      <Input
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.externalName`}
                        label='External name'
                        disabled={!isCurProductSelected}
                      />
                    );
                    const classInput = (
                      <Input
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.class`}
                        label='Class'
                        type='number'
                        disabled={!isCurProductSelected}
                      />
                    );
                    const usageInput = (
                      <Input
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.usage`}
                        type='textarea'
                        label='Usage'
                        disabled={!isCurProductSelected}
                      />
                    );
                    const quantityInput = (
                      <Input
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.quantity`}
                        label='Quantity*'
                        type='number'
                        disabled={!isCurProductSelected}
                        onInputChange={(newQuantity): void => {
                          formikBag.setFieldValue(
                            `${formikSectionType}.${index}.quantity`,
                            Number(newQuantity),
                          );
                        }}
                      />
                    );

                    /** Complex Inputs. */
                    const productInput = (
                      <ApiLookupInput
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        formState={formikBag.values}
                        name={`${formikSectionType}.${index}.product`}
                        label='Product*'
                        route={{
                          barrel: 'GET products',
                          args: (inputText, { constructQueryParams }) =>
                            constructQueryParams(inputText, {
                              filterBy: [
                                {
                                  operation: Enums.FilterOperation.Equals,
                                  name: 'isArchived',
                                  values: [false],
                                },
                                {
                                  name: 'plantId',
                                  operation: Enums.FilterOperation.Equals,
                                  values: [plant?.id ?? ''],
                                },
                              ],
                            }),
                          // If you change this transform, you probably also want to make corresponding changes
                          // to the product's option data generated by QuoteProductToProjectOrQuoteProductFormik().
                          transform: (pl): LookupInputOption[] =>
                            lookups({
                              ...pl,
                              label: (p): string => {
                                if (p.incompatibleMixQuantityUnits) {
                                  return `[UOM issue] ${p.alternateID}`;
                                }
                                return p.alternateID;
                              },
                              sublabels: (p) => [p.name],
                            }),
                        }}
                        onMatchChange={(productID, pl): void => {
                          actions.onProductSelected(productID, pl);
                        }}
                      />
                    );

                    const [tierCurrency, appliedTierType] = calculateTierAdjustmentFormik(
                      formikBag.values,
                      formikSectionType,
                      index,
                      fixedPricingByCompany,
                      plant?.id,
                      formikProducts[index]?.product?.id,
                    );

                    const appliedDiscounts = hasDiscounts ? (formikProduct.discounts ?? []) : [];
                    const rowDiscountTotal = totalDiscountOff(appliedDiscounts);
                    const rowNetPrice =
                      parseFloat(cleanedDecimal(formikProduct.price.number || '0')) || 0;
                    const rowGrossPrice = grossPriceOf(appliedDiscounts, rowNetPrice);
                    const unitPriceInput = (
                      <UnitPrice
                        tierPrice={tierCurrency}
                        tierPriceType={appliedTierType}
                        originalPrice={formikProduct.originalPrice}
                        discounts={appliedDiscounts}
                        grossPrice={rowGrossPrice}
                        discountTotal={rowDiscountTotal}
                        discountCount={appliedDiscounts.length}
                        companyID={companyId}
                        productID={formikProduct.product.id ?? undefined}
                        deliveryType={formikProduct.deliveryType ?? null}
                        currencySymbol={currencySymbol}
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.price`}
                        label={
                          plantProduct?.category === Enums.ProductCategory.Aggregate
                            ? 'Material price*'
                            : 'Unit price*'
                        }
                        disabled={!isCurProductSelected}
                        onPriceChange={(unitPriceNumber, discounts): void => {
                          actions.onUnitPriceChange(
                            unitPriceNumber,
                            hasValue(discounts) ? { discounts } : undefined,
                          );
                        }}
                      />
                    );
                    // eslint-disable-next-line react/no-unstable-nested-components
                    const deliveryCostOverrideInput = ((): ReactElement | null => {
                      if (plantProduct?.category !== Enums.ProductCategory.ReadyMix) {
                        return null;
                      }
                      const endAdornmentUnit = getUnitLabel(
                        formikProduct.deliveryCosts.deliveryCostOverride?.unit,
                      );

                      return (
                        <Input
                          readOnly={readOnly}
                          sx={{ flex: 1, flexBasis: 120 }}
                          name={`${formikSectionType}.${index}.deliveryCosts.deliveryCostOverride`}
                          label='Delivery cost override'
                          type='cost'
                          startAdornment={currencySymbol}
                          endAdornment={`/${endAdornmentUnit}`}
                          onInputChange={async (val): Promise<void> => {
                            actions.onDeliveryCostOverrideChange(val);
                          }}
                        />
                      );
                    })();
                    const pourTimeInput = plantProduct?.category ===
                      Enums.ProductCategory.ReadyMix && (
                      <SingleUnitDurationInput
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.deliveryCosts.pourTime`}
                        label='Pour time'
                        unit='minutes'
                        onInputChange={async (pourTime): Promise<void> => {
                          actions.onPourTimeChange(pourTime);
                        }}
                      />
                    );
                    const waitTimeInput = plantProduct?.category ===
                      Enums.ProductCategory.ReadyMix && (
                      <SingleUnitDurationInput
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.deliveryCosts.waitTime`}
                        label='Wait time'
                        unit='minutes'
                        onInputChange={async (waitTime): Promise<void> => {
                          actions.onWaitTimeChange(waitTime);
                        }}
                      />
                    );
                    const loadSizeInput = plantProduct?.category ===
                      Enums.ProductCategory.ReadyMix && (
                      <Input
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.deliveryCosts.loadSize`}
                        label='Load size'
                        type='number'
                        endAdornment={getUnitLabel(formikProduct.deliveryCosts?.loadSizeUnit)}
                        onInputChange={async (loadSize): Promise<void> => {
                          actions.onLoadSizeChange(loadSize);
                        }}
                      />
                    );

                    const incompatibleBatchUnitSwitch = formikProduct.product
                      .incompatibleMixQuantityUnits === true && (
                      <Box>
                        <StyledSwitch
                          disableStateColor
                          disabled={readOnly}
                          id={`${formikSectionType}.${index}.product.allowIncompatibleMixQuantityUnits`}
                          checked={
                            getIn(formikBag.values, formikSectionType)[index].product
                              .allowIncompatibleMixQuantityUnits
                          }
                          inputProps={{
                            'aria-labelledby': `${formikSectionType}.${index}.product.allowIncompatibleMixQuantityUnits.label`,
                          }}
                          onChange={(_, checked) =>
                            formikBag.setFieldValue(
                              `${formikSectionType}.${index}.product.allowIncompatibleMixQuantityUnits`,
                              checked,
                            )
                          }
                        />
                        <Box>
                          <Typography
                            id={`${formikSectionType}.${index}.product.allowIncompatibleMixQuantityUnits.label`}
                            color={
                              formikProduct.product.allowIncompatibleMixQuantityUnits === true
                                ? undefined
                                : 'error'
                            }
                          >
                            Accept UOM issue? Costs cannot be calculated for this product because
                            some of its mix&apos;s materials use a batch unit that is incompatible
                            with the material&apos;s cost unit.
                          </Typography>
                        </Box>
                      </Box>
                    );
                    const deliveryCostRow = (
                      <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', columnGap: 2 }}>
                        <Box sx={{ flex: 1, flexBasis: 120 }}>{deliveryCostOverrideInput}</Box>

                        {formikProduct.deliveryCosts.deliveryCostOverride !== null &&
                        formikProduct.deliveryCosts.deliveryCostOverride?.amount?.number !== '' ? (
                          <Typography sx={{ flex: 1, flexBasis: 120 }} color='textSecondary'>
                            Detailed cost inputs have been hidden because the calculated delivery
                            cost is overridden here.
                          </Typography>
                        ) : (
                          <>
                            {loadSizeInput}
                            {pourTimeInput}
                            {waitTimeInput}
                            {oneWayTripTimeDisplay}
                            {deliveryCostDisplay}
                          </>
                        )}
                      </Box>
                    );
                    const aggDeliveryTypeInput = (
                      <LocalLookupInput
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        formState={formikBag.values}
                        name={`${formikSectionType}.${index}.deliveryType`}
                        label={requiredLabel('Delivery', requiresProductDeliveryType)}
                        options={PRODUCT_DELIVERY_TYPE_OPTIONS}
                        onMatchChange={(deliveryTypeStr): void => {
                          actions.onDeliveryTypeChange(
                            deliveryTypeStr as Enums.ProductDeliveryType | null,
                          );
                        }}
                      />
                    );
                    const aggTaxRateInput = (
                      <Input
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.taxRate`}
                        label='Tax rate'
                        type='number'
                        max='999.99'
                        precision={2}
                        maxIntegerDigits={3}
                        endAdornment='%'
                        onInputChange={async (taxRate): Promise<void> => {
                          actions.onTaxRateChange(taxRate);
                        }}
                      />
                    );
                    const withDeliverySubtotalPriceField = (
                      <Input
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.withDeliveryDisplayOnly.withDeliverySubtotalPrice`}
                        label='Subtotal'
                        type='text'
                        disabled
                      />
                    );
                    const withDeliveryTotalPriceField = (
                      <Input
                        readOnly={readOnly}
                        sx={{ flex: 1, flexBasis: 120 }}
                        name={`${formikSectionType}.${index}.withDeliveryDisplayOnly.withDeliveryTotalPrice`}
                        label='Total price'
                        type='text'
                        disabled
                      />
                    );

                    const discountPicker =
                      hasDiscounts &&
                      isQuoteSection &&
                      hasStringValue(plant?.id) &&
                      formikProduct.product.id ? (
                        <QuoteProductDiscountPicker
                          companyID={companyId}
                          productID={formikProduct.product.id}
                          deliveryType={formikProduct.deliveryType ?? null}
                          netUnitPrice={rowNetPrice}
                          applied={appliedDiscounts}
                          isPriceSettled={isPriceSettled}
                          currencySymbol={currencySymbol}
                          unitLabel={getUnitLabelWithSlash(plantProduct?.quantityUnit)}
                          readOnly={readOnly}
                          onChange={(applied, net): void => {
                            const netNumber = net.toFixed(4);
                            actions.onUnitPriceChange(formatDecimalString(netNumber) || netNumber, {
                              discounts: applied,
                            });
                          }}
                        />
                      ) : null;

                    const projectOrMainProductsDeliveryCostSection = (
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
                          {quantityInput}
                          {unitPriceInput}
                          {actualMarginDisplay}
                          {marginOverMaterialsDisplay}
                        </Box>

                        <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
                          {targetMarginDisplay}
                          {suggestedPriceDisplay}
                          {listPriceDisplay}
                          {unitCostDisplay}
                        </Box>

                        {plantProduct?.category === Enums.ProductCategory.ReadyMix &&
                          deliveryCostRow}
                      </Box>
                    );

                    const additionalServicesDeliveryCostSection = (
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
                          {unitPriceInput}
                          {actualMarginDisplay}
                          {marginOverMaterialsDisplay}
                        </Box>

                        <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
                          {targetMarginDisplay}
                          {suggestedPriceDisplay}
                          {listPriceDisplay}
                          {unitCostDisplay}
                        </Box>

                        {plantProduct?.category === Enums.ProductCategory.ReadyMix &&
                          deliveryCostRow}
                      </Box>
                    );

                    /**
                     * nonWithDeliverySection will be null if the current product is
                     * a "with delivery" (aggregate or asphalt) product.
                     *
                     * Otherwise it will conditionally show the right format based on
                     * the formikSectionType.
                     */
                    const nonWithDeliverySection =
                      // eslint-disable-next-line no-nested-ternary
                      plantProduct !== null && WithDeliveryProductCategory(plantProduct.category)
                        ? null
                        : formikSectionType.startsWith('additionalProductsByPlants')
                          ? additionalServicesDeliveryCostSection
                          : projectOrMainProductsDeliveryCostSection;

                    /**
                     * withDeliverySection will be null if the current product is
                     * not a "with delivery" (aggregate or asphalt) product.
                     *
                     * Otherwise it will show the "with delivery" product format.
                     */
                    const withDeliverySection =
                      plantProduct !== null &&
                      WithDeliveryProductCategory(plantProduct.category) ? (
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
                            {!formikSectionType.startsWith('additionalProductsByPlants') &&
                              quantityInput}
                            {unitPriceInput}
                            {actualMarginDisplay}
                            {aggDeliveryTypeInput}
                          </Box>

                          {plantProduct?.category === Enums.ProductCategory.Asphalt && (
                            <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
                              {targetMarginDisplay}
                              {suggestedPriceDisplay}
                              {listPriceDisplay}
                              {unitCostDisplay}
                            </Box>
                          )}

                          {plantProduct?.category === Enums.ProductCategory.Aggregate && (
                            <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
                              {listPriceDisplay}
                            </Box>
                          )}

                          {(formikProduct.deliveryType === Enums.ProductDeliveryType.Hauling ||
                            formikProduct.deliveryType ===
                              Enums.ProductDeliveryType.InternalHauling) && (
                            <ProductTruckTypesList
                              isProjectForm={isProjectForm}
                              productObject={plantProduct ?? null}
                              index={index}
                              formikSectionType={formikSectionType}
                              readOnly={readOnly}
                              actions={actions}
                              actionsForAdditionalTruckingInfo={actionsForAdditionalTruckingInfo}
                              addTruckingInfoRow={addTruckingInfoRow}
                              formikProduct={formikProduct}
                            />
                          )}
                          {formikProduct.deliveryType === Enums.ProductDeliveryType.Pickup && (
                            <>
                              {withDeliverySubtotalPriceField}
                              {withDeliveryTotalPriceField}
                              {aggTaxRateInput}
                            </>
                          )}
                        </Box>
                      ) : null;

                    // UI for Project Product drawer
                    if (isProjectForm) {
                      return (
                        <Memo
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${formikSectionType}-grid-${index}`}
                          value={{ formikProduct, plantProduct }}
                        >
                          <Draggable
                            draggableId={`${formikSectionType}-grid-${index}`}
                            index={index}
                          >
                            {(draggableProvided, snapshot) => (
                              <Box
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                style={{
                                  ...draggableProvided.draggableProps.style,
                                  backgroundColor: snapshot.isDragging ? '#eaeaea' : 'white',
                                  boxShadow: snapshot.isDragging
                                    ? '0 4px 8px rgba(0, 0, 0, 0.3)'
                                    : 'none',
                                }}
                              >
                                <Box
                                  paddingY='1.25rem'
                                  display='flex'
                                  flexDirection='column'
                                  gap='1rem'
                                >
                                  <Box sx={{ display: 'flex', gap: 2 }}>
                                    {!readOnly && (
                                      <Button
                                        sx={{ alignSelf: 'center' }}
                                        startIcon={<DragIndicatorIcon />}
                                        {...draggableProvided.dragHandleProps}
                                      />
                                    )}
                                    <Box sx={{ flex: 1 }}>
                                      {moneyDisplaySwitch}

                                      {showNonForecastedWarning && plantProduct && (
                                        <StatusBox
                                          kind={warningKind}
                                          icon={InfoOutlinedIcon}
                                          title='Not included in forecasts'
                                          subtitle={`Products in the ${
                                            plantProduct.category
                                              ? getUserFacingCategoryName(plantProduct.category)
                                              : 'unknown'
                                          } category are not included in project forecasts (${forecastableCategoryDisplayList()} only).`}
                                          sx={{ marginBottom: '1rem' }}
                                        />
                                      )}

                                      <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
                                        {productInput}
                                        {externalNameInput}
                                      </Box>

                                      {usageInput}

                                      <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
                                        {withDeliverySection}
                                        {incompatibleBatchUnitSwitch}
                                      </Box>

                                      {nonWithDeliverySection}
                                      {discountPicker}
                                    </Box>

                                    {!readOnly && (
                                      <Button
                                        sx={{ alignSelf: 'center' }}
                                        startIcon={
                                          <Trash
                                            variant='TwoTone'
                                            color={theme.palette.SlabIndigo['700']}
                                          />
                                        }
                                        onClick={(): void => {
                                          handleRemoveProduct(
                                            index,
                                            formikProducts[index].product.option?.label ?? '',
                                          );
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                                <Divider />
                              </Box>
                            )}
                          </Draggable>
                        </Memo>
                      );
                    }

                    // UI for Quote Product main products Drawer
                    if (formikSectionType.startsWith('mainProductsByPlants')) {
                      return (
                        <Memo
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${formikSectionType}-grid-${index}`}
                          value={{ formikProduct, plantProduct }}
                        >
                          <Draggable
                            draggableId={`${formikSectionType}-grid-${index}`}
                            index={index}
                          >
                            {(draggableProvided, snapshot) => (
                              <Box
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                style={{
                                  ...draggableProvided.draggableProps.style,
                                  backgroundColor: snapshot.isDragging ? '#eaeaea' : 'white',
                                  boxShadow: snapshot.isDragging
                                    ? '0 4px 8px rgba(0, 0, 0, 0.3)'
                                    : 'none',
                                }}
                              >
                                <Box
                                  paddingY='1.25rem'
                                  display='flex'
                                  flexDirection='column'
                                  gap='1rem'
                                >
                                  <Box sx={{ display: 'flex', gap: 2 }}>
                                    {!readOnly && (
                                      <Button
                                        sx={{ alignSelf: 'center' }}
                                        startIcon={<DragIndicatorIcon />}
                                        {...draggableProvided.dragHandleProps}
                                      />
                                    )}

                                    <Box sx={{ flex: 1 }}>
                                      {moneyDisplaySwitch}

                                      <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
                                        {productInput}
                                        {externalNameInput}
                                        {classInput}
                                      </Box>

                                      {usageInput}

                                      <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
                                        {withDeliverySection}
                                        {incompatibleBatchUnitSwitch}
                                      </Box>

                                      {nonWithDeliverySection}
                                      {discountPicker}
                                    </Box>

                                    {!readOnly && (
                                      <Button
                                        sx={{ alignSelf: 'center' }}
                                        startIcon={
                                          <Trash
                                            variant='TwoTone'
                                            color={theme.palette.SlabIndigo['700']}
                                          />
                                        }
                                        onClick={(): void => {
                                          handleRemoveProduct(
                                            index,
                                            formikProducts[index].product.option?.label ?? '',
                                          );
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                                <Divider />
                              </Box>
                            )}
                          </Draggable>
                        </Memo>
                      );
                    }

                    // UI for Quote Product additional services Drawer
                    return (
                      <Memo
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${formikSectionType}-grid-${index}`}
                        value={{ formikProduct, plantProduct }}
                      >
                        <Draggable draggableId={`${formikSectionType}-grid-${index}`} index={index}>
                          {(draggableProvided, snapshot) => (
                            <Box
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              style={{
                                ...draggableProvided.draggableProps.style,
                                backgroundColor: snapshot.isDragging ? '#eaeaea' : 'white',
                                boxShadow: snapshot.isDragging
                                  ? '0 4px 8px rgba(0, 0, 0, 0.3)'
                                  : 'none',
                              }}
                            >
                              <Box
                                paddingY='1.25rem'
                                display='flex'
                                flexDirection='column'
                                gap='1rem'
                              >
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                  {!readOnly && (
                                    <Button
                                      sx={{ alignSelf: 'center' }}
                                      startIcon={<DragIndicatorIcon />}
                                      {...draggableProvided.dragHandleProps}
                                    />
                                  )}

                                  <Box sx={{ flex: 1 }}>
                                    {moneyDisplaySwitch}

                                    {productInput}
                                    {externalNameInput}

                                    <Box sx={{ flex: 1 }}>{usageInput}</Box>

                                    {withDeliverySection}
                                    {incompatibleBatchUnitSwitch}

                                    <Box sx={{ flex: 1 }}>
                                      {nonWithDeliverySection}
                                      {discountPicker}
                                    </Box>
                                  </Box>

                                  {!readOnly && (
                                    <Button
                                      sx={{ alignSelf: 'center' }}
                                      startIcon={
                                        <Trash
                                          variant='TwoTone'
                                          color={theme.palette.SlabIndigo['700']}
                                        />
                                      }
                                      onClick={(): void => {
                                        handleRemoveProduct(
                                          index,
                                          formikProducts[index].product.option?.label ?? '',
                                        );
                                      }}
                                    />
                                  )}
                                </Box>
                                <Divider />
                              </Box>
                            </Box>
                          )}
                        </Draggable>
                      </Memo>
                    );
                  })}

                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          {formikProducts.length === 0 ? (
            <StatusBox
              sx={{ marginBottom: '1rem' }}
              title='Location must have at least one product'
              kind={errorKind}
              icon={ErrorOutlineIcon}
              subtitle='Please add at least one product to continue working with the location.'
            />
          ) : null}

          {!readOnly && (
            <Box>
              <Button
                onClick={(): void => {
                  addRow(defaultIsMoneySwitchOn);
                }}
                sx={{
                  justifyContent: 'flex-start',
                  alignSelf: 'flex-start',
                  textTransform: 'none',
                  color: theme.palette.SlabIndigo['700'],
                }}
                startIcon={<BoxAdd variant='TwoTone' color={theme.palette.SlabIndigo['700']} />}
              >
                Add product
              </Button>
            </Box>
          )}
        </>
      )}
    />
  );
};
